// Shared grouping logic for the Vendor Ledger views (flow diagram + summary
// table). Groups every debt-raising entry (trip_cancelled / bill_overpaid)
// under its origin booking, then works out how much of it has actually been
// settled — a debt raised for ₹3000 might get split and adjusted against two
// different later bookings, so "settled against" can be more than one row.
export function buildLedgerGroups(entries) {
  const debtEntries = entries.filter((e) =>
    ["trip_cancelled", "bill_overpaid"].includes(e.type),
  );

  const groups = [];
  const groupIndexByRefNo = new Map();
  debtEntries.forEach((debt) => {
    const key = debt.ref_no || `unknown-${debt.id}`;
    if (!groupIndexByRefNo.has(key)) {
      groupIndexByRefNo.set(key, groups.length);
      groups.push({ ref_no: debt.ref_no, client_name: debt.client_name, branches: [] });
    }
    groups[groupIndexByRefNo.get(key)].branches.push(debt);
  });

  groups.forEach((group) => {
    group.originalDebt = group.branches
      .filter((d) => d.ref_ledger_id == null)
      .reduce((sum, d) => sum + Math.abs(d.amount), 0);
    group.usedAmount = group.branches
      .filter((d) => d.is_resolved)
      .reduce((sum, d) => sum + (d.resolved_in?.amount || 0), 0);
    group.outstandingAmount = group.branches
      .filter((d) => !d.is_resolved)
      .reduce((sum, d) => sum + Math.abs(d.amount), 0);
    group.origins = group.branches
      .filter((d) => d.ref_ledger_id == null)
      .map((d) => ({
        description: d.description,
        remark: d.remark,
        date: d.date,
        amount: Math.abs(d.amount),
      }));

    // Every place this debt (or a piece of it) actually landed — one entry
    // per branch that got settled, so "3 outstanding debts adjusted into one
    // booking" shows up as 3 separate settledAgainst rows if that's what
    // happened, or fewer if only some branches settled.
    group.settledAgainst = group.branches
      .filter((d) => d.is_resolved)
      .map((d) => ({
        ref_no: d.resolved_in?.ref_no,
        client_name: d.resolved_in?.client_name,
        amount: d.resolved_in?.amount,
        date: d.resolved_in?.date || d.date,
        paymentType: d.resolved_in?.payment_type,
        paidVia: d.resolved_in?.paid_via,
      }));

    const branchIds = new Set(group.branches.map((d) => d.id));
    const settlementRows = entries.filter(
      (e) => e.type === "outstanding_adjusted" && branchIds.has(e.ref_ledger_id),
    );
    const rawTrail = [...group.branches, ...settlementRows].sort((a, b) => a.id - b.id);

    let outstandingSoFar = 0;
    group.trail = rawTrail.map((e) => {
      let eventType, from, to, amount, paymentType, utr, paidVia;
      if (e.type === "outstanding_adjusted") {
        outstandingSoFar -= e.amount;
        eventType = "Settled";
        from = e.resolved_from?.ref_no || "—";
        to = e.ref_no;
        amount = e.amount;
        paymentType = e.payment_type || null;
        utr = e.utr || null;
        const originBranch = group.branches.find((d) => d.id === e.ref_ledger_id);
        paidVia = originBranch?.resolved_in?.paid_via || null;
      } else if (e.ref_ledger_id == null) {
        outstandingSoFar += Math.abs(e.amount);
        eventType = "Debt Raised";
        from = "Vendor";
        to = e.ref_no;
        amount = Math.abs(e.amount);
        paymentType = null;
        utr = null;
        paidVia = null;
      } else {
        eventType = "Leftover Carried";
        from = group.ref_no;
        to = e.ref_no;
        amount = Math.abs(e.amount);
        paymentType = null;
        utr = null;
        paidVia = null;
      }
      return {
        id: e.id,
        date: e.date,
        eventType,
        from,
        to,
        amount,
        paymentType,
        utr,
        paidVia,
        description: e.description,
        remark: e.remark,
        outstandingAfter: Math.max(outstandingSoFar, 0),
      };
    });
  });

  return groups;
}
